import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { notificationTrackerService } from '../services/notificationTrackerService';

const NotificationContext = createContext(null);

// How often to silently re-fetch notifications (milliseconds)
const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes

export const NotificationProvider = ({ userId, children }) => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const hasFetched = useRef(false);

    const fetchNotifications = useCallback(async (uid, silent = false) => {
        if (!uid) return;
        if (!silent) setLoading(true);
        try {
            const [celebrations, warnings] = await Promise.all([
                notificationTrackerService.getUnseenNotificationForTodayBirthdayAndJoinDayByUserId(uid),
                notificationTrackerService.getUnseenNotificationsByUserId(uid),
            ]);

            const merged = [
                ...(Array.isArray(celebrations) ? celebrations : []).map(n => ({ ...n, type: 'celebration' })),
                ...(Array.isArray(warnings) ? warnings : []).map(n => ({ ...n, type: 'warning' })),
            ].sort((a, b) => b.id - a.id);
            
            setNotifications(merged);
        } catch (err) {
            console.error('Failed to fetch notifications', err);
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    // Initial fetch when userId is available
    useEffect(() => {
        if (userId && !hasFetched.current) {
            hasFetched.current = true;
            fetchNotifications(userId);
        }
    }, [userId, fetchNotifications]);

    // Auto-poll: silently re-fetch every POLL_INTERVAL
    useEffect(() => {
        if (!userId) return;
        const intervalId = setInterval(() => {
            fetchNotifications(userId, true);
        }, POLL_INTERVAL);
        return () => clearInterval(intervalId);
    }, [userId, fetchNotifications]);


    const markAsSeen = useCallback(async (notificationId) => {
        try {
            await notificationTrackerService.markNotificationAsSeenById(notificationId);
            setNotifications(prev => prev.filter(n => n.id !== notificationId));
        } catch (err) {
            console.error('Failed to mark notification as seen', err);
        }
    }, []);

    const markAllAsSeen = useCallback(async () => {
        const ids = notifications.map(n => n.id);
        try {
            await Promise.all(ids.map(id => notificationTrackerService.markNotificationAsSeenById(id)));
            setNotifications([]);
        } catch (err) {
            console.error('Failed to mark all notifications as seen', err);
        }
    }, [notifications]);

    const refresh = useCallback(() => {
        hasFetched.current = false;
        fetchNotifications(userId);
    }, [userId, fetchNotifications]);

    return (
        <NotificationContext.Provider
            value={{ notifications, loading, markAsSeen, markAllAsSeen, refresh }}
        >
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotifications = () => {
    const ctx = useContext(NotificationContext);
    if (!ctx) throw new Error('useNotifications must be used inside NotificationProvider');
    return ctx;
};
